import { useState } from "react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { useLang } from "../lib/i18n";

const MONTHS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];

export function DateField({ value, onChange, max, testid, className = "" }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const d = value ? parseISO(value) : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={`tl-input flex items-center gap-2 text-left ${className}`} data-testid={testid}>
          <CalendarDays size={16} className="text-amber-700 shrink-0" />
          {d ? format(d, "dd/MM/yyyy") : <span className="text-slate-400">{t("c.pickDate")}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-[60]" align="start">
        <Calendar mode="single" locale={fr} weekStartsOn={1} selected={d} defaultMonth={d} initialFocus
          disabled={max ? { after: parseISO(max) } : undefined}
          onSelect={(x) => { onChange(x ? format(x, "yyyy-MM-dd") : ""); setOpen(false); }} />
        {value && <button type="button" className="w-full text-xs text-slate-500 py-2 border-t" onClick={() => { onChange(""); setOpen(false); }}>{t("c.clear")}</button>}
      </PopoverContent>
    </Popover>
  );
}

export function MonthField({ value, onChange, testid, className = "" }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [y, m] = (value || "").split("-").map(Number);
  const [year, setYear] = useState(y || new Date().getFullYear());
  const pick = (i) => { onChange(`${year}-${String(i + 1).padStart(2, "0")}`); setOpen(false); };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o && y) setYear(y); }}>
      <PopoverTrigger asChild>
        <button type="button" className={`tl-input flex items-center gap-2 text-left ${className}`} data-testid={testid}>
          <CalendarDays size={16} className="text-amber-700 shrink-0" />
          {value ? `${MONTHS[m - 1]} ${y}` : <span className="text-slate-400">{t("c.allMonths")}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3 z-[60]" align="start">
        <div className="flex items-center justify-between mb-2">
          <button type="button" className="tl-btn tl-btn-ghost tl-btn-sm" onClick={() => setYear(year - 1)} data-testid={`${testid}-prev-year`}><ChevronLeft size={14} /></button>
          <span className="font-bold">{year}</span>
          <button type="button" className="tl-btn tl-btn-ghost tl-btn-sm" onClick={() => setYear(year + 1)} data-testid={`${testid}-next-year`}><ChevronRight size={14} /></button>
        </div>
        <div className="grid grid-cols-3 gap-1">
          {MONTHS.map((n, i) => (
            <button key={n} type="button" onClick={() => pick(i)} data-testid={`${testid}-m${i + 1}`}
              className={`rounded-md py-2 text-sm transition-colors ${year === y && i + 1 === m ? "bg-amber-600 text-white" : "hover:bg-amber-50"}`}>{n}</button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
